/**
 * Pure decision logic for what the client does with a linked catering order
 * when its booking changes.
 *
 * Kept strictly as pure functions so the CateringBookingSyncService can stay
 * thin (observation + execution) while all the "what should happen when"
 * reasoning is unit-testable without mocking subscriptions, timers, or APIs.
 *
 * THE CLIENT NEVER CANCELS CATERING (indoor_mapping_poc-5wkp). A cancelled
 * booking is the server's to act on: `CateringService.bookingLossDecision`
 * in rdx-platform-api reads the vendor's `bookingCancellationAction` and
 * cancellation cut-off, keeps committed orders for review when either says
 * so, and never cancels an order already out for delivery. This module used
 * to answer `auto-cancel` for every live order, which overrode that policy.
 * There is no such action any more.
 *
 * The source of truth for which statuses are "live" (still in a state where a
 * meeting change meaningfully affects the catering order) is exported as
 * LIVE_ORDER_STATUSES — add/remove with care as new statuses are introduced
 * in the backend OrderStatus enum.
 */

import type { CateringOrder, CateringOrderStatus } from '../models/catering.model';

/** Statuses where a linked booking change should still cascade. */
export const LIVE_ORDER_STATUSES: ReadonlyArray<CateringOrderStatus> = [
  'pending_approval',
  'confirmed',
  'in_preparation',
];

/**
 * An order status as the client model spells it.
 *
 * The wire sends the GraphQL enum NAME (`CONFIRMED`, `AT_RISK`); the model
 * and LIVE_ORDER_STATUSES spell the stored value (`confirmed`). Comparing
 * the two directly matched nothing, so no booking change ever reached a
 * decision.
 */
export function normaliseOrderStatus(status: string): string {
  return status.toLowerCase();
}

/** Whether an order in this status (either casing) still follows its meeting. */
export function isLiveOrderStatus(status: string): boolean {
  return (LIVE_ORDER_STATUSES as ReadonlyArray<string>).includes(normaliseOrderStatus(status));
}

/**
 * Change types emitted by the rdx-platform-api bookingChanged subscription.
 * The string values are what the backend sends; treat them as the contract
 * (`bookingChangeType` in the API's schedule-subscriptions.resolver.ts).
 */
export type BookingChangeType = 'created' | 'updated' | 'cancelled' | 'released' | 'moved' | string;

/**
 * Change types that end the booking. The server decides what happens to the
 * catering; the client reads nothing and does nothing.
 */
export function isBookingLoss(changeType: BookingChangeType): boolean {
  return changeType === 'cancelled' || changeType === 'released';
}

export interface BookingChange {
  bookingId: string;
  changeType: BookingChangeType;
  /** ISO strings in UTC — may be absent for non-time changes. */
  startTime?: string;
  endTime?: string;
  spaceId?: string;
}

/** Minimal vendor info required to decide whether a reschedule is allowed. */
export interface VendorCutOffInfo {
  /**
   * Minutes before delivery at which the vendor stops accepting new orders and
   * rescheduled orders. Undefined or 0 means "any time before delivery".
   */
  cutOffMinutes?: number;
}

/**
 * The decision the sync service should execute for a single order given a
 * booking change. None of them writes to the order.
 *
 * - `auto-reschedule`  — booking was moved and new time is still inside the
 *                        vendor's cut-off window. Surfaced as a prompt: there
 *                        is no reschedule mutation.
 * - `prompt-user`      — a reschedule is needed but cut-off has passed OR a
 *                        room change shifted delivery; must not auto-modify.
 *                        The service surfaces a persistent notification.
 * - `no-op`            — nothing for the client to do: nothing relevant
 *                        changed, the order is no longer live, or the booking
 *                        ended and the server is applying the vendor's policy.
 */
export type CascadeAction = 'auto-reschedule' | 'prompt-user' | 'no-op';

export interface CascadeDecision {
  action: CascadeAction;
  /** Target delivery time when action is 'auto-reschedule'. */
  newDeliveryTime?: Date;
  /** Target space when action is 'auto-reschedule' or 'prompt-user' (room change). */
  newSpaceId?: string;
  /** Human-readable reason, suitable for toast/notification copy. */
  reason?: string;
}

/**
 * Decide what to do when a booking change arrives for a specific order.
 *
 * `order.status` may arrive in either casing — see `normaliseOrderStatus`.
 *
 * Reference time defaults to `Date.now()` but is injectable for deterministic
 * testing. All logic is side-effect-free — no API calls, no signal writes.
 */
export function computeCascadeAction(
  order: Pick<CateringOrder, 'status' | 'deliveryTime' | 'deliveryTarget'>,
  change: BookingChange,
  vendor: VendorCutOffInfo,
  now: Date = new Date(),
): CascadeDecision {
  // A cancelled or released booking is the server's decision, whatever the
  // order's status: it may cancel the order, or keep it for the vendor to
  // review.
  if (isBookingLoss(change.changeType)) {
    return { action: 'no-op', reason: "The server applies the vendor's cancellation policy" };
  }

  // Orders not in a live status don't need cascading — they're already done,
  // cancelled, or in a terminal state where the meeting change is irrelevant.
  if (!isLiveOrderStatus(order.status)) {
    return { action: 'no-op', reason: 'Order is not in a live status' };
  }

  if (change.changeType === 'moved' || change.changeType === 'updated') {
    // We only react to time/space deltas. A generic 'updated' with no time
    // change shouldn't disturb catering.
    const newStart = change.startTime ? new Date(change.startTime) : null;
    const newSpaceId = change.spaceId;
    const spaceChanged =
      !!newSpaceId &&
      !!order.deliveryTarget?.spaceId &&
      newSpaceId !== order.deliveryTarget.spaceId;
    const timeChanged =
      !!newStart &&
      !Number.isNaN(newStart.getTime()) &&
      new Date(order.deliveryTime).getTime() !== newStart.getTime();

    if (!timeChanged && !spaceChanged) {
      return { action: 'no-op' };
    }

    // Room-only change: always prompt — delivery address is a courtesy
    // question, not something to auto-redirect.
    if (spaceChanged && !timeChanged) {
      return {
        action: 'prompt-user',
        newSpaceId,
        reason: 'Meeting moved to a different room — confirm delivery target',
      };
    }

    // Time change (with or without room change): gate on cut-off window.
    // timeChanged === true implies newStart is set and valid.
    if (!newStart) return { action: 'no-op' };
    const targetTime = newStart;
    const cutOffMinutes = vendor.cutOffMinutes ?? 0;
    const earliestAllowed = new Date(now.getTime() + cutOffMinutes * 60_000);

    if (targetTime.getTime() < earliestAllowed.getTime()) {
      return {
        action: 'prompt-user',
        newDeliveryTime: targetTime,
        newSpaceId: spaceChanged ? newSpaceId : undefined,
        reason:
          cutOffMinutes > 0
            ? `New meeting time is inside the vendor's ${cutOffMinutes}-minute cut-off window`
            : 'New meeting time has already passed',
      };
    }

    return {
      action: 'auto-reschedule',
      newDeliveryTime: targetTime,
      newSpaceId: spaceChanged ? newSpaceId : undefined,
      // Nothing has moved the delivery yet: the prompt asks the user to.
      reason: 'Meeting time changed — confirm the new delivery time',
    };
  }

  // 'created' and unknown change types don't cascade.
  return { action: 'no-op' };
}
